import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { exportScanPDF } from "@/lib/pdf";
import { PageHeader, severityStyle } from "@/components/shared";
import { Button } from "@/components/ui/button";
import {
  ScanSearch,
  Zap,
  HardDrive,
  Smartphone,
  ShieldCheck,
  Bug,
  CheckCircle2,
  FileDown,
} from "lucide-react";

const SCAN_TYPES = [
  { key: "quick", label: "Quick Scan", icon: Zap, desc: "Fast check of critical areas" },
  { key: "full", label: "Full System", icon: HardDrive, desc: "Deep scan of all files" },
  { key: "mobile", label: "Mobile Scan", icon: Smartphone, desc: "Apps, SMS & network" },
];

const STEPS = [
  "Initializing scan engine...",
  "Loading threat signatures [1.2M]...",
  "Scanning system files...",
  "Inspecting active processes...",
  "Analyzing network connections...",
  "Checking browser & cache...",
  "Cross-referencing threat database...",
  "Compiling report...",
];

export default function Scanner() {
  const [scanType, setScanType] = useState("quick");
  const [scanning, setScanning] = useState(false);
  const [lines, setLines] = useState([]);
  const [result, setResult] = useState(null);
  const termRef = useRef(null);

  useEffect(() => {
    if (termRef.current) termRef.current.scrollTop = termRef.current.scrollHeight;
  }, [lines]);

  const runScan = async () => {
    setScanning(true);
    setResult(null);
    setLines([]);
    let i = 0;
    const interval = setInterval(() => {
      if (i < STEPS.length) {
        const step = STEPS[i];
        setLines((prev) => [...prev, { t: `[${String(i + 1).padStart(2, "0")}]`, msg: step }]);
        i++;
      }
    }, 320);

    try {
      const [{ data }] = await Promise.all([
        api.post("/scan", { scan_type: scanType, target: "This Device" }),
        new Promise((res) => setTimeout(res, STEPS.length * 320 + 300)),
      ]);
      clearInterval(interval);
      setLines((prev) => [
        ...prev,
        { t: ">>", msg: `Scan complete. ${data.files_scanned.toLocaleString()} items scanned.`, done: true },
      ]);
      setResult(data);
    } catch (e) {
      clearInterval(interval);
      setLines((prev) => [...prev, { t: "!!", msg: "Scan failed. Try again.", err: true }]);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <PageHeader
        tag="Detection Engine"
        title="Threat Scanner"
        subtitle="Scan your device for trojans, malware, spyware and vulnerabilities. Detected threats are quarantined automatically."
      />

      {/* Scan type selector */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {SCAN_TYPES.map((t) => {
          const Icon = t.icon;
          const active = scanType === t.key;
          return (
            <button
              key={t.key}
              data-testid={`scan-type-${t.key}`}
              disabled={scanning}
              onClick={() => setScanType(t.key)}
              className={`text-left p-5 border transition-[border-color,background-color,transform] duration-200 hover:-translate-y-px disabled:opacity-50 ${
                active ? "border-primary bg-primary/10 glow-green" : "border-border bg-card hover:border-primary/50"
              }`}
            >
              <Icon size={22} strokeWidth={1.5} className={active ? "text-primary" : "text-muted-foreground"} />
              <div className="font-mono font-bold mt-3">{t.label}</div>
              <div className="text-xs text-muted-foreground mt-1">{t.desc}</div>
            </button>
          );
        })}
      </div>

      <Button
        data-testid="run-scan-button"
        onClick={runScan}
        disabled={scanning}
        size="lg"
        className="w-full sm:w-auto rounded-none font-mono bg-primary text-primary-foreground hover:bg-primary/90 glow-green mb-8"
      >
        <ScanSearch size={18} strokeWidth={1.5} className="mr-2" />
        {scanning ? "Scanning..." : "Start Scan"}
      </Button>

      {/* Terminal */}
      {(scanning || lines.length > 0) && (
        <div className="relative bg-[#060906] border border-border scanlines mb-6">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-card">
            <span className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#FFD700]/60" />
            <span className="w-2.5 h-2.5 rounded-full bg-primary/60" />
            <span className="font-mono text-xs text-muted-foreground ml-2">plyx@guard:~/scan/{scanType}</span>
          </div>
          <div ref={termRef} data-testid="scan-terminal" className="relative z-[2] p-5 h-64 overflow-y-auto font-mono text-sm space-y-1">
            {lines.map((l, i) => (
              <div key={i} className={`flex gap-3 animate-fade-up ${l.err ? "text-destructive" : l.done ? "text-primary" : "text-muted-foreground"}`}>
                <span className="text-primary/60 shrink-0">{l.t}</span>
                <span>{l.msg}</span>
              </div>
            ))}
            {scanning && (
              <div className="flex gap-2 text-primary">
                <span className="animate-pulse-glow">▊</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div data-testid="scan-result" className="animate-fade-up">
          <div className={`flex items-center gap-4 p-6 border mb-4 ${result.threats_found > 0 ? "border-destructive/50 bg-destructive/10" : "border-primary/50 bg-primary/10"}`}>
            {result.threats_found > 0 ? (
              <Bug size={32} strokeWidth={1.5} className="text-destructive shrink-0" />
            ) : (
              <ShieldCheck size={32} strokeWidth={1.5} className="text-primary shrink-0" />
            )}
            <div className="flex-1">
              <div className={`font-mono font-bold text-xl ${result.threats_found > 0 ? "text-destructive" : "text-primary"}`}>
                {result.threats_found > 0
                  ? `${result.threats_found} threat${result.threats_found === 1 ? "" : "s"} neutralized`
                  : "No threats found — system clean"}
              </div>
              <div className="font-mono text-sm text-muted-foreground mt-1">
                {result.files_scanned.toLocaleString()} items scanned · security score {result.score}
              </div>
            </div>
            <Button
              data-testid="export-pdf-button"
              onClick={() => exportScanPDF(result)}
              variant="outline"
              className="rounded-none font-mono border-border hover:border-primary hover:text-primary shrink-0"
            >
              <FileDown size={16} strokeWidth={1.5} className="mr-2" /> Export PDF
            </Button>
          </div>

          {result.findings.length > 0 && (
            <div className="border border-border bg-card divide-y divide-border">
              {result.findings.map((f, i) => (
                <div key={i} data-testid={`finding-${i}`} className="p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`px-2 py-1 border font-mono text-[10px] uppercase tracking-widest shrink-0 ${severityStyle(f.severity)}`}>
                      {f.type}
                    </div>
                    <div>
                      <div className="font-mono text-sm text-foreground">{f.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{f.detail}</div>
                      <div className="text-[11px] text-muted-foreground/70 font-mono mt-1">{f.location}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-primary font-mono text-xs shrink-0">
                    <CheckCircle2 size={14} strokeWidth={1.5} /> Quarantined
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
